// src/Admin.js
import React, { useCallback, useEffect, useState } from "react";
import { auth, signInWithGoogle, signOut, onAuthStateChanged } from "./firebase";
import appIcon from "./assets/landing/app-icon.png";
import LinksTab from "./admin/LinksTab";
import CoversTab from "./admin/CoversTab";
import LaunchBoxTab from "./admin/LaunchBoxTab";
import PricingTab from "./admin/PricingTab";
import QueueTab from "./admin/QueueTab";
import IgdbTab from "./admin/IgdbTab";
import UsersTab from "./admin/UsersTab";
import DatabaseTab from "./admin/DatabaseTab";
import "./LandingPage.css";
import "./Admin.css";

// Sidebar structure. Each tab carries its own component so adding one is a single
// entry here — no second switch to keep in sync in the render below. Groups are the
// unit of future growth: a new area of the panel gets its own group, not a loose tab.
const TAB_GROUPS = [
  {
    key: "games",
    label: "Games",
    tabs: [
      { key: "links", label: "Links", component: LinksTab },
      { key: "covers", label: "TheGamesDb", component: CoversTab },
      { key: "launchbox", label: "LaunchBox", component: LaunchBoxTab },
      { key: "igdb", label: "IGDB", component: IgdbTab },
    ],
  },
  {
    key: "pricing",
    label: "Pricing",
    tabs: [
      { key: "pricing", label: "Pricing", component: PricingTab },
      { key: "queue", label: "Queue", component: QueueTab },
    ],
  },
  {
    key: "social",
    label: "Social Network",
    tabs: [
      { key: "users", label: "Users", component: UsersTab },
    ],
  },
  {
    key: "catalog",
    label: "Catalog",
    tabs: [
      { key: "database", label: "Database update", component: DatabaseTab },
    ],
  },
];

const ALL_TABS = TAB_GROUPS.flatMap((group) => group.tabs);

function GoogleGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="store-button-glyph" aria-hidden="true">
      <path
        fill="currentColor"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.55c2.08-1.92 3.29-4.74 3.29-8.1Z"
      />
      <path
        fill="currentColor"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.55-2.77c-.98.66-2.24 1.06-3.73 1.06-2.87 0-5.3-1.94-6.17-4.53H2.18v2.85A11 11 0 0 0 12 23Z"
      />
      <path
        fill="currentColor"
        d="M5.83 14.1A6.6 6.6 0 0 1 5.48 12c0-.73.13-1.44.35-2.1V7.05H2.18A11 11 0 0 0 1 12c0 1.77.43 3.45 1.18 4.95l3.65-2.85Z"
      />
      <path
        fill="currentColor"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1a11 11 0 0 0-9.82 6.05l3.65 2.85C6.7 7.32 9.13 5.38 12 5.38Z"
      />
    </svg>
  );
}

function GoogleSignInButton({ onClick }) {
  return (
    <button className="store-button admin-google-btn" onClick={onClick}>
      <GoogleGlyph />
      <span className="store-button-copy">
        <small>Sign in with</small>
        Google
      </span>
    </button>
  );
}

function Admin() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [activeTab, setActiveTab] = useState("links");
  const ActiveTabComponent = ALL_TABS.find((tab) => tab.key === activeTab)?.component;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setAuthChecked(true);
    });
    return unsubscribe;
  }, []);

  const handleForbidden = useCallback(() => setForbidden(true), []);

  return (
    <div className="landing admin-shell">
      <div className="landing-backdrop" aria-hidden="true" />
      <div className="landing-veil" aria-hidden="true" />

      <header className="topbar">
        <div className="topbar-inner glass">
          <div className="brand">
            <img src={appIcon} alt="" className="brand-mark" />
            <span>GameZ Admin</span>
          </div>
          {user && !forbidden && (
            <div className="admin-account">
              <span className="admin-user">{user.email}</span>
              <button className="admin-signout-btn" onClick={() => signOut(auth)}>
                Sign out
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="admin-main">
        {!authChecked && null}

        {authChecked && !user && (
          <div className="admin-gate glass">
            <p className="kicker kicker--center">Restricted access</p>
            <h2>Sign in to continue</h2>
            <GoogleSignInButton onClick={signInWithGoogle} />
          </div>
        )}

        {authChecked && user && forbidden && (
          <div className="admin-gate glass">
            <p className="kicker kicker--center">Not authorized</p>
            <h2>{user.email} can't access this panel</h2>
            <p className="admin-gate-copy">
              Your Firebase UID: <code>{user.uid}</code>
            </p>
            <p className="admin-gate-copy">
              Set <code>ADMIN_FIREBASE_UID</code> to this value in the API's .env to grant yourself access.
            </p>
            <button className="admin-signout-btn" onClick={() => signOut(auth)}>
              Sign out
            </button>
          </div>
        )}

        {authChecked && user && !forbidden && (
          <div className="admin-layout">
            <nav className="admin-sidebar glass">
              {TAB_GROUPS.map((group) => (
                <div className="admin-tab-group" key={group.key}>
                  <span className="admin-tab-group-label">{group.label}</span>
                  {group.tabs.map((tab) => (
                    <button
                      key={tab.key}
                      className={`admin-tab ${activeTab === tab.key ? "admin-tab-active" : ""}`}
                      onClick={() => setActiveTab(tab.key)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              ))}
            </nav>

            <div className="admin-content">
              {ActiveTabComponent && <ActiveTabComponent user={user} onForbidden={handleForbidden} />}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Admin;
